import { Badge, Button, Checkbox, DatePicker, Empty, Modal, Radio, Select, Spin, Tabs, Tag, Tooltip, message } from "antd";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, Music, Users } from "lucide-react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import React, { useMemo, useState } from "react";
import { useGetServiceTickets, useUpdateServiceTicket } from "~/services/service-ticket";
import { useGetVesselsForDropdown } from "~/services/vessel";
import { useGetAllStaff } from "~/services/operation";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { SortOrder, ServiceType, StaffAvailability } from "~/utils/constants";
import { ContainerInfoCard } from "~/components/common/container-info";
import { formatStaffName } from "~/utils/format-staff-name";
import { StaffDetailsSidebar } from "~/components/admin/operations/staffings/list-staffs/staff-details-sidebar";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface BookingCalendarViewProps {
  setViewMode: (mode: string) => void;
  bookings?: any[];
  isLoading?: boolean;
}

export const BookingCalendarView: React.FC<BookingCalendarViewProps> = ({
  setViewMode,
  bookings = [],
  isLoading: externalLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState("all");
  const [viewMode, setViewModeLocal] = useState<"day" | "2day" | "4day" | "week" | "schedule" | "scheduleweek">("week");
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [draggedService, setDraggedService] = useState<any>(null);
  const [dragOverSlot, setDragOverSlot] = useState<{ date: string; slot: number } | null>(null);
  const [scrollInterval, setScrollInterval] = useState<NodeJS.Timeout | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [rescheduleModalVisible, setRescheduleModalVisible] = useState(false);
  const [serviceToReschedule, setServiceToReschedule] = useState<any>(null);
  const [newDateTime, setNewDateTime] = useState<dayjs.Dayjs | null>(null);

  // Mozart mode state
  const [mozartMode, setMozartMode] = useState(false);
  const [mozartTab, setMozartTab] = useState<'vessel' | 'staff' | 'datetime'>('vessel');
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(new Set());
  const [selectedVesselId, setSelectedVesselId] = useState<string | undefined>(undefined);

  // Staff assignment state
  const [staffOperation, setStaffOperation] = useState<'add' | 'replace' | 'remove'>('add');
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);

  // Date/Time shift state
  const [dateShiftType, setDateShiftType] = useState<'forward' | 'backward' | 'specific'>('forward');
  const [shiftAmount, setShiftAmount] = useState<number>(1);
  const [shiftUnit, setShiftUnit] = useState<'day' | 'hour' | 'minute'>('day');
  const [specificDate, setSpecificDate] = useState<dayjs.Dayjs | null>(null);
  const [specificTime, setSpecificTime] = useState<dayjs.Dayjs | null>(null);

  // Confirmation modal state
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    type: 'vessel' | 'staff' | 'datetime';
    data?: any;
  }>({ type: 'vessel' });

  // Staff details sidebar state
  const [staffDetailsDate, setStaffDetailsDate] = useState<dayjs.Dayjs | null>(null);

  // Mutation for updating service ticket
  const { mutateAsync: updateServiceTicket } = useUpdateServiceTicket();

  // Auto-scroll when dragging near edges
  const handleDragScroll = (e: React.DragEvent, scrollContainer: HTMLElement | null) => {
    if (!scrollContainer || !draggedService) return;

    const threshold = 100; // pixels from edge to trigger scroll
    const scrollSpeed = 10; // pixels per interval
    const rect = scrollContainer.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;

    // Clear existing interval
    if (scrollInterval) {
      clearInterval(scrollInterval);
      setScrollInterval(null);
    }

    // Scroll up if near top
    if (mouseY < threshold && mouseY > 0) {
      const interval = setInterval(() => {
        scrollContainer.scrollTop -= scrollSpeed;
      }, 20);
      setScrollInterval(interval);
    }
    // Scroll down if near bottom
    else if (mouseY > rect.height - threshold && mouseY < rect.height) {
      const interval = setInterval(() => {
        scrollContainer.scrollTop += scrollSpeed;
      }, 20);
      setScrollInterval(interval);
    }
  };

  // Clean up scroll interval
  React.useEffect(() => {
    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [scrollInterval]);

  // Configuration for slot-based layout
  const SLOTS_PER_HOUR = 4; // 15-minute intervals
  const SLOT_HEIGHT = 18; // pixels per slot (sweet spot between 16 and 20)

  // Calculate columns per day based on view mode
  const columnsPerDay = useMemo(() => {
    switch (viewMode) {
      case "day":
        return 4; // 4 columns for day view
      case "2day":
        return 2; // 2 columns for 2-day view
      case "4day":
        return 1; // 1 column for 4-day view
      case "week":
        return 1; // 1 column for week view
      default:
        return 1;
    }
  }, [viewMode]);

  // Time slots for calendar grid (7am to 7pm)
  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 7; hour <= 19; hour++) {
      const time = dayjs().hour(hour).minute(0).second(0);
      slots.push({
        hour,
        label: time.format("h A"),
        fullLabel: time.format("h:mm A"),
        slots: SLOTS_PER_HOUR
      });
    }
    return slots;
  }, []);

  // API call for service tickets
  const apiDateRange = useMemo(() => {
    if (viewMode === "day") {
      return {
        startDate: selectedDate.startOf("day").toISOString(),
        endDate: selectedDate.endOf("day").toISOString(),
      };
    } else if (viewMode === "2day") {
      return {
        startDate: selectedDate.startOf("day").toISOString(),
        endDate: selectedDate.add(1, "day").endOf("day").toISOString(),
      };
    } else if (viewMode === "4day") {
      return {
        startDate: selectedDate.startOf("day").toISOString(),
        endDate: selectedDate.add(3, "day").endOf("day").toISOString(),
      };
    } else { // week
      return {
        startDate: selectedDate.startOf("week").toISOString(),
        endDate: selectedDate.endOf("week").toISOString(),
      };
    }
  }, [selectedDate, viewMode]);

  const { data, isLoading, refetch } = useGetServiceTickets({
    page: 1,
    pageSize: 100,
    startDate: apiDateRange.startDate,
    endDate: apiDateRange.endDate,
    sortByCreatedAt: SortOrder.DESC,
  });

  // Get vessels and staff for Mozart mode
  const { data: vesselsData } = useGetVesselsForDropdown();
  const { data: staffData } = useGetAllStaff();

  // Staff availability data for staff details modal
  const staffAvailabilityDateRange = useMemo(() => {
    if (viewMode === "schedule") {
      return {
        startDate: selectedDate.startOf("day").toISOString(),
        endDate: selectedDate.endOf("day").toISOString(),
      };
    } else if (viewMode === "scheduleweek") {
      return {
        startDate: selectedDate.startOf("week").toISOString(),
        endDate: selectedDate.endOf("week").toISOString(),
      };
    }
    return { startDate: "", endDate: "" };
  }, [selectedDate, viewMode]);

  const { data: staffAvailabilityData } = useGetStaffAvailabilities(
    {
      startDate: staffAvailabilityDateRange.startDate,
      endDate: staffAvailabilityDateRange.endDate,
      pageSize: 500,
    },
    (viewMode === "schedule" || viewMode === "scheduleweek") && !!staffAvailabilityDateRange.startDate
  );

  // Process staff availability data into a map by date
  const staffAvailabilityMap = useMemo(() => {
    const map: Record<string, {
      staffInfo: Array<{
        staffId: string;
        availability: StaffAvailability;
        note?: string;
        staffName?: string;
        isDriver?: boolean;
        isLead?: boolean;
        bookedHours?: number;
        availableHours?: number;
      }>;
      pendingStaffInfo: Array<{
        staffId: string;
        staffName?: string;
        isDriver?: boolean;
        isLead?: boolean;
      }>;
      availableCount: number;
      unavailableCount: number;
      pendingCount: number;
    }> = {};

    if (staffAvailabilityData?.items) {
      // Group by date
      staffAvailabilityData.items.forEach((schedule: any) => {
        if (!schedule?.date) return;
        const dateKey = dayjs(schedule.date).format("YYYY-MM-DD");

        if (!map[dateKey]) {
          map[dateKey] = {
            staffInfo: [],
            pendingStaffInfo: [],
            availableCount: 0,
            unavailableCount: 0,
            pendingCount: 0,
          };
        }

        const staffName = formatStaffName(schedule.staff);
        map[dateKey].staffInfo.push({
          staffId: schedule.staffId,
          availability: schedule.availability || StaffAvailability.FULL_TIME,
          note: schedule.note || undefined,
          staffName,
          isDriver: schedule.staff?.driver || false,
          isLead: schedule.staff?.lead || false,
          bookedHours: schedule.bookedHours || 0,
          availableHours: schedule.availableHours || 8,
        });

        if (schedule.availability === StaffAvailability.FULL_TIME ||
            schedule.availability === StaffAvailability.PART_TIME) {
          map[dateKey].availableCount += 1;
        } else if (schedule.availability === StaffAvailability.UNAVAILABLE) {
          map[dateKey].unavailableCount += 1;
        }
      });

      // Calculate pending staff for each date
      if (staffData?.items) {
        const dates = viewMode === "schedule"
          ? [selectedDate]
          : Array.from({ length: 7 }, (_, i) => selectedDate.startOf("week").add(i, "day"));

        dates.forEach(date => {
          const dateKey = date.format("YYYY-MM-DD");
          if (!map[dateKey]) {
            map[dateKey] = {
              staffInfo: [],
              pendingStaffInfo: [],
              availableCount: 0,
              unavailableCount: 0,
              pendingCount: 0,
            };
          }

          const submittedStaffIds = new Set(map[dateKey].staffInfo.map(s => s.staffId));

          staffData.items.forEach((staff: any) => {
            const isActiveStaff = staff.status !== 'HIATUS' && staff.status !== 'INACTIVE';
            if (!submittedStaffIds.has(staff.id) && isActiveStaff) {
              map[dateKey].pendingCount += 1;
              map[dateKey].pendingStaffInfo.push({
                staffId: staff.id,
                staffName: formatStaffName(staff),
                isDriver: staff.driver || false,
                isLead: staff.lead || false,
              });
            }
          });
        });
      }
    }

    return map;
  }, [staffAvailabilityData, staffData, selectedDate, viewMode]);

  // Get staff data for a specific date
  const getStaffDataForDate = (date: dayjs.Dayjs) => {
    const dateKey = date.format("YYYY-MM-DD");
    return staffAvailabilityMap[dateKey] || {
      staffInfo: [],
      pendingStaffInfo: [],
      availableCount: 0,
      unavailableCount: 0,
      pendingCount: 0,
    };
  };

  // Generate days array based on view mode
  const viewDays = useMemo(() => {
    const days = [];

    switch (viewMode) {
      case "day":
      case "schedule":
        days.push(selectedDate);
        break;
      case "2day":
        for (let i = 0; i < 2; i++) {
          days.push(selectedDate.add(i, "day"));
        }
        break;
      case "4day":
        for (let i = 0; i < 4; i++) {
          days.push(selectedDate.add(i, "day"));
        }
        break;
      case "week":
      case "scheduleweek": {
        const startOfWeek = selectedDate.startOf("week");
        for (let i = 0; i < 7; i++) {
          days.push(startOfWeek.add(i, "day"));
        }
        break;
      }
      default:
        days.push(selectedDate);
        break;
    }

    return days;
  }, [viewMode, selectedDate]);

  // Filter services based on active tab (from raw API data)
  const tabFilteredServices = useMemo(() => {
    if (!data?.items) return [];
    
    let filtered;
    switch (activeTab) {
      case "transport":
        filtered = data.items.filter(
          (service) =>
            service.serviceType === "COLLECTION" ||
            service.serviceType === "DELIVERY" ||
            service.serviceType === ServiceType.COLLECTION ||
            service.serviceType === ServiceType.DELIVERY
        );
        break;
      case "site":
        filtered = data.items.filter(
          (service) => 
            service.serviceType === "ON_SITE_SERVICE" ||
            service.serviceType === "SITE_SERVICE" ||
            service.serviceType === ServiceType.SITE_SERVICE
        );
        break;
      case "warehouse":
        filtered = data.items.filter(
          (service) =>
            service.serviceType === "WAREHOUSE_SERVICE" ||
            service.serviceType === "FABRICATION_SERVICE" ||
            service.serviceType === ServiceType.WAREHOUSE_SERVICE ||
            service.serviceType === ServiceType.FABRICATION_SERVICE
        );
        break;
      case "all":
      default:
        filtered = data.items;
        break;
    }
    
    return filtered;
  }, [data?.items, activeTab]);

  // Filter services to only include ones that fall within our view range
  const filteredServices = useMemo(() => {
    const startOfRange = viewDays[0];
    const endOfRange = viewDays[viewDays.length - 1];
    
    return tabFilteredServices.filter(service => {
      const scheduledDate = service.serviceDatetime;
      const serviceDateTime = scheduledDate || service.startDatetime;
      
      if (!serviceDateTime) {
        return false;
      }
      
      const serviceDate = dayjs(serviceDateTime);
      const isInRange = serviceDate.isSameOrAfter(startOfRange, 'day') && 
                       serviceDate.isSameOrBefore(endOfRange, 'day');
      
      return isInRange;
    });
  }, [tabFilteredServices, viewDays]);

  // Helper function to calculate slots occupied by a service
  const calculateServiceSlots = (service: any) => {
    const duration = service.duration || 1; // duration in hours
    const slotsNeeded = Math.ceil(duration * SLOTS_PER_HOUR);
    const minSlots = SLOTS_PER_HOUR; // Minimum 1 hour (4 slots)
    return Math.max(slotsNeeded, minSlots);
  };

  // Group the filtered services by date and starting slot position with column assignment
  const servicesByDateAndTime = useMemo(() => {
    const grouped = new Map<string, Map<number, any[]>>(); // Map<dateKey, Map<slotIndex, services[]>>
    const overflowServices = new Map<string, { early: any[], late: any[] }>();
    const servicesByDay = new Map<string, any[]>(); // All services for each day

    filteredServices.forEach((service) => {
      const scheduledDate = service.serviceDatetime || service.startDatetime;
      const actualTime = service.startDatetime;

      if (scheduledDate && actualTime) {
        const serviceDate = dayjs(scheduledDate);
        const timeWithHour = dayjs(actualTime);
        const dateKey = serviceDate.format("YYYY-MM-DD");
        const hour = timeWithHour.hour();
        const minute = timeWithHour.minute();

        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, new Map());
        }

        if (!overflowServices.has(dateKey)) {
          overflowServices.set(dateKey, { early: [], late: [] });
        }

        if (!servicesByDay.has(dateKey)) {
          servicesByDay.set(dateKey, []);
        }

        // Check if service is within main time slots (7am-7pm)
        if (hour < 7) {
          overflowServices.get(dateKey)!.early.push(service);
        } else if (hour >= 19) {
          overflowServices.get(dateKey)!.late.push(service);
        } else {
          // Calculate which slot this service starts in
          // Slot 0 = 7:00am, Slot 1 = 7:15am, etc.
          const hoursFromStart = hour - 7;
          const slotIndex = (hoursFromStart * SLOTS_PER_HOUR) + Math.floor(minute / (60 / SLOTS_PER_HOUR));

          // Store service with metadata
          const serviceWithSlots = {
            ...service,
            slotIndex,
            slotsOccupied: calculateServiceSlots(service)
          };

          servicesByDay.get(dateKey)!.push(serviceWithSlots);
        }
      }
    });

    // Assign columns for each day to prevent overlaps
    servicesByDay.forEach((services, dateKey) => {
      const dayMap = grouped.get(dateKey)!;
      const columnOccupancy: number[] = Array(columnsPerDay).fill(-1);

      // Sort by start slot
      services.sort((a, b) => a.slotIndex - b.slotIndex);

      services.forEach((service) => {
        const startSlot = service.slotIndex;
        const endSlot = startSlot + service.slotsOccupied - 1;

        // Find first available column
        let assignedColumn = -1;
        for (let col = 0; col < columnsPerDay; col++) {
          if (columnOccupancy[col] < startSlot) {
            assignedColumn = col;
            columnOccupancy[col] = endSlot;
            break;
          }
        }

        // If no column available, use first column (overlap)
        if (assignedColumn === -1) {
          assignedColumn = 0;
          columnOccupancy[0] = endSlot;
        }

        service.columnIndex = assignedColumn;

        // Add to slot map
        if (!dayMap.has(service.slotIndex)) {
          dayMap.set(service.slotIndex, []);
        }
        dayMap.get(service.slotIndex)!.push(service);
      });
    });

    return { grouped, overflowServices };
  }, [filteredServices, columnsPerDay]);

  // Calculate tab counts based on services actually in the view range
  const tabCounts = useMemo(() => {
    if (!tabFilteredServices) return { all: 0, transport: 0, site: 0, warehouse: 0 };
    
    const startOfRange = viewDays[0];
    const endOfRange = viewDays[viewDays.length - 1];
    
    const servicesInRange = tabFilteredServices.filter(service => {
      const serviceDateTime = service.serviceDatetime || service.startDatetime;
      if (!serviceDateTime) return false;
      
      const serviceDate = dayjs(serviceDateTime);
      return serviceDate.isSameOrAfter(startOfRange, 'day') && 
             serviceDate.isSameOrBefore(endOfRange, 'day');
    });
    
    const all = servicesInRange.length;
    const transport = servicesInRange.filter(
      (service) =>
        service.serviceType === "COLLECTION" ||
        service.serviceType === "DELIVERY" ||
        service.serviceType === ServiceType.COLLECTION ||
        service.serviceType === ServiceType.DELIVERY
    ).length;
    const site = servicesInRange.filter(
      (service) => 
        service.serviceType === "ON_SITE_SERVICE" ||
        service.serviceType === "SITE_SERVICE" ||
        service.serviceType === ServiceType.SITE_SERVICE
    ).length;
    const warehouse = servicesInRange.filter(
      (service) =>
        service.serviceType === "WAREHOUSE_SERVICE" ||
        service.serviceType === "FABRICATION_SERVICE" ||
        service.serviceType === ServiceType.WAREHOUSE_SERVICE ||
        service.serviceType === ServiceType.FABRICATION_SERVICE
    ).length;

    return { all, transport, site, warehouse };
  }, [tabFilteredServices, viewDays]);

  // Service card component for grid - using ContainerInfoCard with slot-based sizing
  const GridServiceCard = ({ service, compact = false, columnIndex = 0, totalColumns = 1 }: { service: any; compact?: boolean; columnIndex?: number; totalColumns?: number }) => {
    const slotsOccupied = service.slotsOccupied || calculateServiceSlots(service);
    const cardHeight = (slotsOccupied * SLOT_HEIGHT) - 4; // 4px margin

    // Calculate width and position based on column layout
    const columnWidth = totalColumns > 1 ? `${100 / totalColumns}%` : '100%';
    const leftPosition = totalColumns > 1 ? `${(columnIndex / totalColumns) * 100}%` : '0';

    return (
      <div
        className="absolute cursor-move select-none"
        draggable={true}
        onDragStart={(e) => {
          e.stopPropagation();
          setDraggedService(service);
          e.dataTransfer.effectAllowed = 'move';
          // Create a drag image
          const dragImage = e.currentTarget.cloneNode(true) as HTMLElement;
          dragImage.style.position = 'absolute';
          dragImage.style.top = '-1000px';
          document.body.appendChild(dragImage);
          e.dataTransfer.setDragImage(dragImage, 0, 0);
          setTimeout(() => document.body.removeChild(dragImage), 0);
          // Add a semi-transparent effect
          if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = '0.4';
          }
        }}
        onDragEnd={(e) => {
          if (e.currentTarget instanceof HTMLElement) {
            e.currentTarget.style.opacity = '1';
          }
          if (scrollInterval) {
            clearInterval(scrollInterval);
            setScrollInterval(null);
          }
          setDraggedService(null);
          setDragOverSlot(null);
        }}
        style={{
          height: `${cardHeight}px`,
          width: `calc(${columnWidth} - 4px)`,
          left: leftPosition,
          top: 0,
          marginLeft: '2px',
          marginRight: '2px',
          zIndex: draggedService?.id === service.id ? 1000 : 10,
        }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedService(service);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setServiceToReschedule(service);
          setNewDateTime(dayjs(service.startDatetime));
          setRescheduleModalVisible(true);
        }}
      >
        <ContainerInfoCard
          serviceTicket={service}
          variant="compact"
          showHeader={false}
          className="border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-xs h-full pointer-events-none"
        />
      </div>
    );
  };

  // Overflow service component (for early/late services) - using ContainerInfoCard
  const OverflowServiceCard = ({ service }: { service: any }) => {
    return (
      <div 
        className="mb-1 cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedService(service);
        }}
      >
        <ContainerInfoCard
          serviceTicket={service}
          variant="compact"
          showHeader={false}
          className="border border-gray-300 bg-gray-100 hover:bg-gray-200 transition-colors text-xs"
        />
      </div>
    );
  };

  // Handle manual reschedule from modal
  const handleManualReschedule = async () => {
    if (!serviceToReschedule || !newDateTime) return;

    try {
      await updateServiceTicket({
        id: serviceToReschedule.id,
        serviceDatetime: newDateTime.toISOString(),
        startDatetime: newDateTime.toISOString(),
      });

      message.success(`Service rescheduled to ${newDateTime.format('MMM D, YYYY h:mm A')}`);
      setRescheduleModalVisible(false);
      setServiceToReschedule(null);
      setNewDateTime(null);

      // Refresh calendar data
      await refetch();
    } catch (error) {
      message.error('Failed to reschedule service');
      console.error('Error rescheduling service:', error);
    }
  };

  // Handle drop event to update service date/time
  const handleDrop = async (dateKey: string, slotIndex: number) => {
    if (!draggedService) return;

    // Store reference before clearing state
    const serviceToUpdate = draggedService;

    // Clear drag state immediately to prevent UI glitches
    setDraggedService(null);
    setDragOverSlot(null);

    // Calculate new date and time from slot
    const newDate = dayjs(dateKey);
    const hoursFromStart = Math.floor(slotIndex / SLOTS_PER_HOUR);
    const minutesInSlot = (slotIndex % SLOTS_PER_HOUR) * (60 / SLOTS_PER_HOUR);
    const newHour = 7 + hoursFromStart;
    const newDateTime = newDate.hour(newHour).minute(minutesInSlot).second(0);

    // Check if actually moving to a different time
    const currentDateTime = dayjs(serviceToUpdate.startDatetime);
    if (currentDateTime.isSame(newDateTime, 'minute')) {
      return; // No change needed
    }

    try {
      // Update the service ticket
      await updateServiceTicket({
        id: serviceToUpdate.id,
        serviceDatetime: newDateTime.toISOString(),
        startDatetime: newDateTime.toISOString(),
      });

      message.success(`Service moved to ${newDateTime.format('MMM D, h:mm A')}`);

      // Refresh calendar data
      await refetch();
    } catch (error) {
      message.error('Failed to update service time');
      console.error('Error updating service:', error);
    }
  };

  // Navigation functions
  const navigatePrevious = () => {
    switch (viewMode) {
      case "day":
      case "schedule":
        setSelectedDate(selectedDate.subtract(1, "day"));
        break;
      case "2day":
        setSelectedDate(selectedDate.subtract(2, "day"));
        break;
      case "4day":
        setSelectedDate(selectedDate.subtract(4, "day"));
        break;
      case "week":
      case "scheduleweek":
        setSelectedDate(selectedDate.subtract(1, "week"));
        break;
    }
  };

  const navigateNext = () => {
    switch (viewMode) {
      case "day":
      case "schedule":
        setSelectedDate(selectedDate.add(1, "day"));
        break;
      case "2day":
        setSelectedDate(selectedDate.add(2, "day"));
        break;
      case "4day":
        setSelectedDate(selectedDate.add(4, "day"));
        break;
      case "week":
      case "scheduleweek":
        setSelectedDate(selectedDate.add(1, "week"));
        break;
    }
  };

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const tabItems = [
    { key: "all", label: `All (${tabCounts.all})` },
    { key: "transport", label: `Transport (${tabCounts.transport})` },
    { key: "site", label: `Site (${tabCounts.site})` },
    { key: "warehouse", label: `Warehouse (${tabCounts.warehouse})` },
  ];

  // Mozart mode handlers
  const toggleMozartMode = () => {
    setMozartMode(!mozartMode);
    if (mozartMode) {
      // Exiting Mozart mode, clear all selections
      setSelectedServiceIds(new Set());
      setSelectedVesselId(undefined);
      setSelectedStaffIds([]);
      setMozartTab('vessel');
      setStaffOperation('add');
      setDateShiftType('forward');
      setShiftAmount(1);
      setShiftUnit('day');
      setSpecificDate(null);
      setSpecificTime(null);
    }
  };

  const handleCheckboxChange = (serviceId: string, checked: boolean) => {
    setSelectedServiceIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(serviceId);
      } else {
        newSet.delete(serviceId);
      }
      return newSet;
    });
  };

  const handleBulkUpdateVessel = async () => {
    if (selectedServiceIds.size === 0) {
      message.warning('Please select at least one service ticket');
      return;
    }

    if (!selectedVesselId) {
      message.warning('Please select a vessel');
      return;
    }

    try {
      message.loading('Updating service tickets...', 0);

      const updatePromises = Array.from(selectedServiceIds).map(serviceId =>
        updateServiceTicket({ id: serviceId, vesselId: selectedVesselId })
      );

      await Promise.all(updatePromises);

      message.destroy();
      message.success(`Successfully updated ${selectedServiceIds.size} service ticket(s)`);

      // Clear selections
      setSelectedServiceIds(new Set());
      setSelectedVesselId(undefined);

      // Refetch data
      await refetch();
    } catch (error) {
      message.destroy();
      console.error('Error updating service tickets:', error);
      message.error('Failed to update service tickets');
    }
  };

  const handleBulkUpdateStaff = async () => {
    if (selectedServiceIds.size === 0) {
      message.warning('Please select at least one service ticket');
      return;
    }

    if (selectedStaffIds.length === 0 && staffOperation !== 'remove') {
      message.warning('Please select at least one staff member');
      return;
    }

    try {
      message.loading('Updating staff assignments...', 0);

      const updatePromises = Array.from(selectedServiceIds).map(async (serviceId) => {
        // Get current service to access existing staff
        const currentService = filteredServices.find(s => s.id === serviceId);
        const currentStaffIds = currentService?.staffs?.map((s: any) => s.id) || [];

        let newStaffIds: string[];

        switch (staffOperation) {
          case 'add':
            // Add new staff to existing
            newStaffIds = [...new Set([...currentStaffIds, ...selectedStaffIds])];
            break;
          case 'replace':
            // Replace all staff with selected
            newStaffIds = selectedStaffIds;
            break;
          case 'remove':
            // Remove selected staff from existing
            newStaffIds = currentStaffIds.filter(id => !selectedStaffIds.includes(id));
            break;
          default:
            newStaffIds = currentStaffIds;
        }

        return updateServiceTicket({ id: serviceId, staffIds: newStaffIds });
      });

      await Promise.all(updatePromises);

      message.destroy();
      message.success(`Successfully updated ${selectedServiceIds.size} service ticket(s)`);

      // Clear selections
      setSelectedServiceIds(new Set());
      setSelectedStaffIds([]);

      // Refetch data
      await refetch();
    } catch (error) {
      message.destroy();
      console.error('Error updating staff assignments:', error);
      message.error('Failed to update staff assignments');
    }
  };

  const handleBulkDateShift = async () => {
    if (selectedServiceIds.size === 0) {
      message.warning('Please select at least one service ticket');
      return;
    }

    if (dateShiftType === 'specific' && !specificDate) {
      message.warning('Please select a date');
      return;
    }

    try {
      message.loading('Updating service dates...', 0);

      const updatePromises = Array.from(selectedServiceIds).map(async (serviceId) => {
        const currentService = filteredServices.find(s => s.id === serviceId);
        if (!currentService) return;

        let newServiceDatetime: dayjs.Dayjs;
        let newStartDatetime: dayjs.Dayjs;

        const currentServiceDate = dayjs(currentService.serviceDatetime);
        const currentStartDate = dayjs(currentService.startDatetime);

        if (dateShiftType === 'specific') {
          // Set to specific date, preserve time if only date selected
          if (specificTime) {
            newServiceDatetime = specificDate!.hour(specificTime.hour()).minute(specificTime.minute());
            newStartDatetime = specificDate!.hour(specificTime.hour()).minute(specificTime.minute());
          } else {
            newServiceDatetime = specificDate!.hour(currentServiceDate.hour()).minute(currentServiceDate.minute());
            newStartDatetime = specificDate!.hour(currentStartDate.hour()).minute(currentStartDate.minute());
          }
        } else {
          // Shift forward or backward
          const multiplier = dateShiftType === 'forward' ? 1 : -1;
          newServiceDatetime = currentServiceDate.add(multiplier * shiftAmount, shiftUnit);
          newStartDatetime = currentStartDate.add(multiplier * shiftAmount, shiftUnit);
        }

        return updateServiceTicket({
          id: serviceId,
          serviceDatetime: newServiceDatetime.toISOString(),
          startDatetime: newStartDatetime.toISOString(),
        });
      });

      await Promise.all(updatePromises);

      message.destroy();
      message.success(`Successfully updated ${selectedServiceIds.size} service ticket(s)`);

      // Clear selections
      setSelectedServiceIds(new Set());
      setSpecificDate(null);
      setSpecificTime(null);

      // Refetch data
      await refetch();
    } catch (error) {
      message.destroy();
      console.error('Error updating service dates:', error);
      message.error('Failed to update service dates');
    }
  };

  // Open calendar in new browser window
  const openCalendarPopup = () => {
    const params = new URLSearchParams({
      date: selectedDate.format('YYYY-MM-DD'),
      viewMode: viewMode,
      activeTab: activeTab,
    });

    const width = 1400;
    const height = 900;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    window.open(
      `/calendar-popup?${params.toString()}`,
      'CalendarPopup',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
  };

  // Calendar content component
  const CalendarContent = () => (
    <>
      {/* Header with Navigation */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center space-x-4">
          <Button type="primary" onClick={() => setViewMode("list")}>
            Switch to List View
          </Button>
          
          {/* Date Navigation */}
          <div className="flex items-center space-x-2">
            <Button 
              type="text" 
              icon={<ChevronLeft size={16} />}
              onClick={navigatePrevious}
              className="hover:bg-gray-100"
            />
            
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
              format="ddd, MMM DD, YYYY"
              className="border-0 text-center cursor-pointer hover:bg-gray-50"
              suffixIcon={null}
              allowClear={false}
            />
            
            <Button 
              type="text" 
              icon={<ChevronRight size={16} />}
              onClick={navigateNext}
              className="hover:bg-gray-100"
            />
          </div>
        </div>

        {/* View Mode Buttons */}
        <div className="flex flex-col items-end space-y-2">
          {/* Calendar Grid Views */}
          <div className="flex items-center space-x-2">
            {[
              { key: "day", label: "Day" },
              { key: "2day", label: "2 Days" },
              { key: "4day", label: "4 Days" },
              { key: "week", label: "Week" }
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === key
                    ? "bg-blue-600 text-white"
                    : "text-blue-600 hover:bg-blue-50 border border-blue-200"
                }`}
                onClick={() => setViewModeLocal(key as any)}
              >
                {label}
              </button>
            ))}

            <Button
              icon={<Maximize2 size={16} />}
              onClick={openCalendarPopup}
              className="ml-2"
            >
              Open in New Window
            </Button>
          </div>

          {/* Schedule List Views */}
          <div className="flex items-center space-x-2">
            {[
              { key: "schedule", label: "Schedule" },
              { key: "scheduleweek", label: "Schedule Week" }
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === key
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:bg-gray-50 border border-gray-200"
                }`}
                onClick={() => setViewModeLocal(key as any)}
              >
                {label}
              </button>
            ))}

            {/* Mozart Mode Button - show in schedule and schedule week views */}
            {(viewMode === "schedule" || viewMode === "scheduleweek") && (
              <Button
                type={mozartMode ? "primary" : "default"}
                icon={<Music size={16} />}
                onClick={toggleMozartMode}
                className={mozartMode ? "bg-purple-600 hover:bg-purple-700 border-purple-600" : ""}
              >
                Mozart {mozartMode && `(${selectedServiceIds.size})`}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Service Type Tabs */}
      <div className="mb-3">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          className="service-tabs"
        />
      </div>

      {/* Mozart Mode Controls */}
      {mozartMode && (viewMode === "schedule" || viewMode === "scheduleweek") && (
        <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-purple-900">
                {selectedServiceIds.size} service{selectedServiceIds.size !== 1 ? 's' : ''} selected
              </span>
              {selectedServiceIds.size > 0 && (
                <Button
                  size="small"
                  onClick={() => setSelectedServiceIds(new Set())}
                >
                  Clear Selection
                </Button>
              )}
            </div>
          </div>

          <Tabs
            activeKey={mozartTab}
            onChange={(key) => setMozartTab(key as 'vessel' | 'staff' | 'datetime')}
            items={[
              {
                key: 'vessel',
                label: 'Vessel',
                children: (
                  <div className="flex items-center space-x-3 py-2">
                    <span className="text-sm font-medium text-purple-900">Update Vessel:</span>
                    <Select
                      style={{ width: 200 }}
                      placeholder="Select vessel"
                      disabled={selectedServiceIds.size === 0}
                      value={selectedVesselId}
                      onChange={setSelectedVesselId}
                      allowClear
                      showSearch
                      filterOption={(input, option) => {
                        const label = option?.label || option?.children;
                        if (typeof label === 'string') {
                          return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }
                        return false;
                      }}
                    >
                      {vesselsData?.map((vessel: any) => (
                        <Select.Option key={vessel.id} value={vessel.id}>
                          {vessel.name} - {vessel.vesselRef}
                        </Select.Option>
                      ))}
                    </Select>
                    <Button
                      type="primary"
                      disabled={!selectedVesselId || selectedServiceIds.size === 0}
                      onClick={() => {
                        const vesselName = vesselsData?.find((v: any) => v.id === selectedVesselId)?.name;
                        setConfirmModalConfig({
                          type: 'vessel',
                          data: { vesselName }
                        });
                        setConfirmModalVisible(true);
                      }}
                    >
                      Update Vessel
                    </Button>
                  </div>
                ),
              },
              {
                key: 'staff',
                label: 'Staff',
                children: (
                  <div className="py-2 space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-purple-900">Operation:</span>
                      <Radio.Group value={staffOperation} onChange={(e) => setStaffOperation(e.target.value)}>
                        <Radio.Button value="add">Add Staff</Radio.Button>
                        <Radio.Button value="replace">Replace Staff</Radio.Button>
                        <Radio.Button value="remove">Remove Staff</Radio.Button>
                      </Radio.Group>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-purple-900">Select Staff:</span>
                      <Select
                        mode="multiple"
                        style={{ width: 400 }}
                        placeholder="Select staff members"
                        disabled={selectedServiceIds.size === 0}
                        value={selectedStaffIds}
                        onChange={setSelectedStaffIds}
                        showSearch
                        filterOption={(input, option) => {
                          const label = option?.label;
                          if (typeof label === 'string') {
                            return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                          }
                          return false;
                        }}
                      >
                        {staffData?.items?.map((staff: any) => {
                          const displayName = staff.user ? `${staff.user.firstName || ''} ${staff.user.lastName || ''}`.trim() : 'Unknown';
                          const tags = [];
                          if (staff.lead) tags.push('L');
                          if (staff.driver) tags.push('D');
                          const tagStr = tags.length > 0 ? ` (${tags.join(',')})` : '';

                          return (
                            <Select.Option key={staff.id} value={staff.id} label={displayName}>
                              {displayName}{tagStr}
                            </Select.Option>
                          );
                        })}
                      </Select>
                      <Button
                        type="primary"
                        disabled={(selectedStaffIds.length === 0 && staffOperation !== 'remove') || selectedServiceIds.size === 0}
                        onClick={() => {
                          const staffNames = selectedStaffIds.map(id => {
                            const staff = staffData?.items?.find((s: any) => s.id === id);
                            return staff?.user ? `${staff.user.firstName || ''} ${staff.user.lastName || ''}`.trim() : 'Unknown';
                          });
                          setConfirmModalConfig({
                            type: 'staff',
                            data: { staffNames, operation: staffOperation }
                          });
                          setConfirmModalVisible(true);
                        }}
                      >
                        Update Staff
                      </Button>
                    </div>
                  </div>
                ),
              },
              {
                key: 'datetime',
                label: 'Date/Time Shift',
                children: (
                  <div className="py-2 space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-purple-900">Shift Type:</span>
                      <Radio.Group value={dateShiftType} onChange={(e) => setDateShiftType(e.target.value)}>
                        <Radio.Button value="forward">Move Forward</Radio.Button>
                        <Radio.Button value="backward">Move Backward</Radio.Button>
                        <Radio.Button value="specific">Set Specific Date</Radio.Button>
                      </Radio.Group>
                    </div>

                    {dateShiftType !== 'specific' && (
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-purple-900">Duration:</span>
                        <Select value={shiftAmount} onChange={setShiftAmount} style={{ width: 80 }}>
                          {[1, 2, 3, 4, 5, 6, 7, 14, 21, 30].map(n => (
                            <Select.Option key={n} value={n}>{n}</Select.Option>
                          ))}
                        </Select>
                        <Select value={shiftUnit} onChange={setShiftUnit} style={{ width: 100 }}>
                          <Select.Option value="minute">Minute{shiftAmount !== 1 ? 's' : ''}</Select.Option>
                          <Select.Option value="hour">Hour{shiftAmount !== 1 ? 's' : ''}</Select.Option>
                          <Select.Option value="day">Day{shiftAmount !== 1 ? 's' : ''}</Select.Option>
                        </Select>
                      </div>
                    )}

                    {dateShiftType === 'specific' && (
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-purple-900">Set Date:</span>
                        <DatePicker
                          value={specificDate}
                          onChange={setSpecificDate}
                          format="MMM D, YYYY"
                        />
                        <span className="text-sm font-medium text-purple-900">Time (optional):</span>
                        <DatePicker
                          value={specificTime}
                          onChange={setSpecificTime}
                          picker="time"
                          format="h:mm A"
                        />
                      </div>
                    )}

                    <div className="flex items-center space-x-3">
                      <Button
                        type="primary"
                        disabled={selectedServiceIds.size === 0 || (dateShiftType === 'specific' && !specificDate)}
                        onClick={() => {
                          setConfirmModalConfig({
                            type: 'datetime',
                            data: {
                              shiftType: dateShiftType,
                              shiftAmount,
                              shiftUnit,
                              specificDate,
                              specificTime
                            }
                          });
                          setConfirmModalVisible(true);
                        }}
                      >
                        Apply Date Shift
                      </Button>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}

      {/* Content based on view mode */}
      <Spin spinning={isLoading || externalLoading}>
        {(viewMode === "schedule" || viewMode === "scheduleweek") ? (
          // SCHEDULE LIST VIEWS with optional sidebar
          <div className="flex gap-4">
            <div className="flex-1 space-y-4">
            {(() => {
              // Get services for schedule views
              const getScheduleServices = () => {
                if (viewMode === "schedule") {
                  // Single day schedule
                  return tabFilteredServices.filter(service => {
                    const serviceDate = dayjs(service.serviceDatetime || service.startDatetime);
                    return serviceDate.isSame(selectedDate, 'day');
                  });
                } else {
                  // Week schedule
                  const startOfWeek = selectedDate.startOf('week');
                  const endOfWeek = selectedDate.endOf('week');
                  return tabFilteredServices.filter(service => {
                    const serviceDate = dayjs(service.serviceDatetime || service.startDatetime);
                    return serviceDate.isSameOrAfter(startOfWeek, 'day') &&
                           serviceDate.isSameOrBefore(endOfWeek, 'day');
                  });
                }
              };

              const scheduleServices = getScheduleServices();

              // Group and sort services
              const sortedServices = [...scheduleServices].sort((a, b) => {
                // For scheduleweek, first sort by day
                if (viewMode === "scheduleweek") {
                  const dayA = dayjs(a.serviceDatetime || a.startDatetime).startOf('day');
                  const dayB = dayjs(b.serviceDatetime || b.startDatetime).startOf('day');
                  if (!dayA.isSame(dayB, 'day')) {
                    return dayA.isBefore(dayB) ? -1 : 1;
                  }
                }

                // PRIMARY SORT: Sort by time first - use serviceDatetime if available, fallback to startDatetime
                const timeA = dayjs(a.serviceDatetime || a.startDatetime);
                const timeB = dayjs(b.serviceDatetime || b.startDatetime);

                // If times are different, sort by time
                if (!timeA.isSame(timeB)) {
                  return timeA.isBefore(timeB) ? -1 : 1;
                }

                // SECONDARY SORT: If same time, sort by service type priority
                const getServiceTypePriority = (serviceType: string) => {
                  switch (serviceType) {
                    case ServiceType.COLLECTION:
                    case ServiceType.DELIVERY:
                      return 1;
                    case ServiceType.ON_SITE_SERVICE:
                      return 2;
                    case ServiceType.WAREHOUSE_SERVICE:
                      return 3;
                    case ServiceType.FABRICATION_SERVICE:
                      return 4;
                    default:
                      return 5;
                  }
                };

                const priorityA = getServiceTypePriority(a.serviceType);
                const priorityB = getServiceTypePriority(b.serviceType);

                if (priorityA !== priorityB) {
                  return priorityA - priorityB;
                }

                // TERTIARY SORT: If same time and type, sort by vessel name
                if ((a.serviceType === ServiceType.COLLECTION || a.serviceType === ServiceType.DELIVERY) &&
                    (b.serviceType === ServiceType.COLLECTION || b.serviceType === ServiceType.DELIVERY)) {
                  const vesselA = a.vessel?.name || '';
                  const vesselB = b.vessel?.name || '';
                  return vesselA.localeCompare(vesselB);
                }

                return 0;
              });

              // Group by day for scheduleweek
              if (viewMode === "scheduleweek") {
                const groupedByDay = new Map<string, any[]>();
                sortedServices.forEach(service => {
                  const dayKey = dayjs(service.serviceDatetime || service.startDatetime).format('YYYY-MM-DD');
                  if (!groupedByDay.has(dayKey)) {
                    groupedByDay.set(dayKey, []);
                  }
                  groupedByDay.get(dayKey)!.push(service);
                });

                return (
                  <>
                    {Array.from(groupedByDay.entries()).map(([dayKey, dayServices], index) => {
                      const dayStaffData = getStaffDataForDate(dayjs(dayKey));
                      const scheduledCount = dayStaffData.staffInfo.filter(s =>
                        (s.availability === StaffAvailability.FULL_TIME || s.availability === StaffAvailability.PART_TIME) &&
                        (s.bookedHours || 0) > 0
                      ).length;
                      const availableCount = dayStaffData.staffInfo.filter(s =>
                        (s.availability === StaffAvailability.FULL_TIME || s.availability === StaffAvailability.PART_TIME) &&
                        (s.bookedHours || 0) === 0
                      ).length;

                      return (
                      <div key={dayKey} className="mb-6">
                        <div
                          className="sticky bg-white z-10 mb-3 pb-2 border-b-2 border-gray-300 px-2 py-1 -mx-2 -my-1 rounded flex justify-between items-start"
                          style={{ top: `${index * 60}px` }}
                        >
                          <div
                            className="cursor-pointer hover:bg-gray-50 transition-colors flex-1 rounded px-1"
                            onClick={() => {
                              setSelectedDate(dayjs(dayKey));
                              setViewModeLocal('schedule');
                            }}
                            title={`Click to view ${dayjs(dayKey).format('MMM D, YYYY')} in schedule view`}
                          >
                            <h3 className="text-lg font-bold text-gray-900">
                              {dayjs(dayKey).format('dddd, MMMM D, YYYY')}
                            </h3>
                            <p className="text-sm text-gray-600">{dayServices.length} service{dayServices.length !== 1 ? 's' : ''}</p>
                          </div>
                          <Button
                            size="small"
                            icon={<Users size={14} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setStaffDetailsDate(dayjs(dayKey));
                            }}
                            className="ml-2 flex items-center"
                          >
                            <span className="text-xs ml-1">
                              <span className="text-blue-600">{scheduledCount}</span>
                              {" / "}
                              <span className="text-green-600">{availableCount}</span>
                            </span>
                          </Button>
                        </div>
                        <div className="space-y-4 mb-8">
                          {dayServices.map(service => (
                            <div key={service.id} className="flex items-start space-x-3">
                              {mozartMode && (
                                <Checkbox
                                  checked={selectedServiceIds.has(service.id)}
                                  onChange={(e) => handleCheckboxChange(service.id, e.target.checked)}
                                  className="mt-3"
                                />
                              )}
                              <div className="flex-1">
                                <ContainerInfoCard
                                  serviceTicket={service}
                                  variant="simpleschedule"
                                  showHeader={false}
                                  className="hover:shadow-md transition-shadow"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      );
                    })}
                    {groupedByDay.size === 0 && <Empty description="No services scheduled for this week" />}
                  </>
                );
              } else {
                // Single day schedule view
                const dayStaffData = getStaffDataForDate(selectedDate);
                const scheduledCount = dayStaffData.staffInfo.filter(s =>
                  (s.availability === StaffAvailability.FULL_TIME || s.availability === StaffAvailability.PART_TIME) &&
                  (s.bookedHours || 0) > 0
                ).length;
                const availableCount = dayStaffData.staffInfo.filter(s =>
                  (s.availability === StaffAvailability.FULL_TIME || s.availability === StaffAvailability.PART_TIME) &&
                  (s.bookedHours || 0) === 0
                ).length;

                return (
                  <>
                    {/* Day Header with Staff Metrics */}
                    <div className="mb-4 pb-3 border-b-2 border-gray-300 flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {selectedDate.format('dddd, MMMM D, YYYY')}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {sortedServices.length} service{sortedServices.length !== 1 ? 's' : ''} scheduled
                        </p>
                      </div>
                      <Button
                        size="small"
                        icon={<Users size={14} />}
                        onClick={() => setStaffDetailsDate(selectedDate)}
                        className="flex items-center"
                      >
                        <span className="text-xs ml-1">
                          <span className="text-blue-600">{scheduledCount}</span>
                          {" / "}
                          <span className="text-green-600">{availableCount}</span>
                        </span>
                      </Button>
                    </div>

                    {/* Services List */}
                    {sortedServices.length > 0 ? (
                      sortedServices.map(service => (
                        <div key={service.id} className="flex items-start space-x-3 mb-4">
                          {mozartMode && (
                            <Checkbox
                              checked={selectedServiceIds.has(service.id)}
                              onChange={(e) => handleCheckboxChange(service.id, e.target.checked)}
                              className="mt-3"
                            />
                          )}
                          <div className="flex-1">
                            <ContainerInfoCard
                              serviceTicket={service}
                              variant="simpleschedule"
                              showHeader={false}
                              className="hover:shadow-md transition-shadow"
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <Empty description="No services scheduled for this day" />
                    )}
                  </>
                );
              }
            })()}
            </div>

            {/* Staff Details Sidebar */}
            {staffDetailsDate && (
              <div className="w-[380px] shrink-0 border-l pl-4">
                <div className="sticky top-0">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-semibold text-gray-900">Staff Details</h3>
                    <Button
                      type="text"
                      size="small"
                      onClick={() => setStaffDetailsDate(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </Button>
                  </div>
                  <StaffDetailsSidebar
                    selectedDate={staffDetailsDate}
                    staffInfo={getStaffDataForDate(staffDetailsDate).staffInfo}
                    pendingStaffInfo={getStaffDataForDate(staffDetailsDate).pendingStaffInfo}
                    availableCount={getStaffDataForDate(staffDetailsDate).availableCount}
                    unavailableCount={getStaffDataForDate(staffDetailsDate).unavailableCount}
                    pendingCount={getStaffDataForDate(staffDetailsDate).pendingCount}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          // CALENDAR GRID VIEWS
          <div ref={scrollContainerRef} className="overflow-auto" style={{ maxHeight: '800px' }}>
            <div className="min-w-[800px]">
            {/* Day headers - Sticky */}
            <div className="grid border-b border-gray-200 bg-gray-50 sticky top-0 z-10" style={{ gridTemplateColumns: `80px repeat(${viewDays.length}, 1fr)` }}>
              <div className="p-2 bg-gray-50"></div> {/* Empty cell for time column */}
              {viewDays.map((day, index) => {
                const isToday = day.isSame(dayjs(), 'day');
                const dayKey = day.format("YYYY-MM-DD");
                const dayTimeMap = servicesByDateAndTime.grouped.get(dayKey) || new Map();
                const overflowData = servicesByDateAndTime.overflowServices.get(dayKey) || { early: [], late: [] };
                const totalServices = Array.from(dayTimeMap.values()).reduce((sum, services) => sum + services.length, 0) + 
                                    overflowData.early.length + overflowData.late.length;
                
                return (
                  <div
                    key={index}
                    className={`py-1.5 px-2 text-center border-l border-gray-200 cursor-pointer transition-colors ${
                      isToday ? 'bg-blue-50 hover:bg-blue-100' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                    onClick={() => {
                      setSelectedDate(day);
                      setViewModeLocal('day');
                    }}
                    title={`Click to view ${day.format('MMM D, YYYY')} in day view`}
                  >
                    <div className="text-sm font-semibold text-gray-800 leading-tight">
                      {day.format("ddd")}
                    </div>
                    <div className={`text-lg font-bold leading-tight ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                      {day.format("MMM D")}
                    </div>
                    <div className="text-xs text-gray-600 leading-tight">
                      {totalServices} service{totalServices !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Early services (before 7am) - Abbreviated */}
            <div className="grid border-b border-gray-200 bg-yellow-50" style={{ gridTemplateColumns: `80px repeat(${viewDays.length}, 1fr)` }}>
              <div className="py-1 px-2 text-xs font-medium text-gray-600 border-r border-gray-200 bg-yellow-100">
                Before 7AM
              </div>
              {viewDays.map((day, dayIndex) => {
                const dayKey = day.format("YYYY-MM-DD");
                const overflowData = servicesByDateAndTime.overflowServices.get(dayKey) || { early: [], late: [] };

                return (
                  <div key={dayIndex} className="p-1 border-l border-gray-200 min-h-[36px]">
                    {overflowData.early.map(service => (
                      <OverflowServiceCard key={service.id} service={service} />
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Main time grid (7am-7pm) - Slot-based rendering */}
            {timeSlots.map((timeSlot) => {
              const hourStartSlot = (timeSlot.hour - 7) * SLOTS_PER_HOUR;

              return (
                <div key={timeSlot.hour} className="grid border-b border-gray-200" style={{ gridTemplateColumns: `80px repeat(${viewDays.length}, 1fr)` }}>
                  {/* Time label - spans all slots for this hour */}
                  <div className="p-2 text-sm font-medium text-gray-600 border-r border-gray-200 bg-gray-50 flex items-start" style={{ height: `${SLOTS_PER_HOUR * SLOT_HEIGHT}px` }}>
                    {timeSlot.label}
                  </div>

                  {/* Day columns with slot grid */}
                  {viewDays.map((day, dayIndex) => {
                    const dayKey = day.format("YYYY-MM-DD");
                    const dayTimeMap = servicesByDateAndTime.grouped.get(dayKey) || new Map();

                    // Collect all services for this hour block
                    const servicesInHour: any[] = [];
                    for (let slotOffset = 0; slotOffset < SLOTS_PER_HOUR; slotOffset++) {
                      const absoluteSlot = hourStartSlot + slotOffset;
                      const servicesAtSlot = dayTimeMap.get(absoluteSlot) || [];
                      servicesAtSlot.forEach((service: any) => {
                        // Only add if this is the starting slot
                        if (service.slotIndex === absoluteSlot) {
                          servicesInHour.push(service);
                        }
                      });
                    }

                    return (
                      <div key={dayIndex} className="border-l border-gray-200 relative" style={{ height: `${SLOTS_PER_HOUR * SLOT_HEIGHT}px` }}>
                        {/* Render slots with grid lines and drop zones */}
                        {Array(SLOTS_PER_HOUR).fill(null).map((_, slotOffset) => {
                          const absoluteSlot = hourStartSlot + slotOffset;
                          const isOver = dragOverSlot?.date === dayKey && dragOverSlot?.slot === absoluteSlot;

                          return (
                            <div
                              key={slotOffset}
                              className={`border-b border-gray-100 transition-colors ${isOver ? 'bg-blue-100' : ''}`}
                              style={{ height: `${SLOT_HEIGHT}px` }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.dataTransfer.dropEffect = 'move';
                                setDragOverSlot({ date: dayKey, slot: absoluteSlot });
                                handleDragScroll(e, scrollContainerRef.current);
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (scrollInterval) {
                                  clearInterval(scrollInterval);
                                  setScrollInterval(null);
                                }
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (scrollInterval) {
                                  clearInterval(scrollInterval);
                                  setScrollInterval(null);
                                }
                                handleDrop(dayKey, absoluteSlot);
                              }}
                            />
                          );
                        })}

                        {/* Render services positioned absolutely */}
                        {servicesInHour.map(service => {
                          // Calculate top position based on which slot within this hour
                          const slotWithinHour = service.slotIndex - hourStartSlot;
                          const topPosition = slotWithinHour * SLOT_HEIGHT;

                          return (
                            <div
                              key={service.id}
                              className="absolute"
                              style={{ top: `${topPosition}px`, left: 0, right: 0 }}
                            >
                              <GridServiceCard
                                service={service}
                                compact={viewDays.length > 4}
                                columnIndex={service.columnIndex || 0}
                                totalColumns={columnsPerDay}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Late services (after 7pm) - Abbreviated */}
            <div className="grid border-b border-gray-200 bg-purple-50" style={{ gridTemplateColumns: `80px repeat(${viewDays.length}, 1fr)` }}>
              <div className="py-1 px-2 text-xs font-medium text-gray-600 border-r border-gray-200 bg-purple-100">
                After 7PM
              </div>
              {viewDays.map((day, dayIndex) => {
                const dayKey = day.format("YYYY-MM-DD");
                const overflowData = servicesByDateAndTime.overflowServices.get(dayKey) || { early: [], late: [] };

                return (
                  <div key={dayIndex} className="p-1 border-l border-gray-200 min-h-[36px]">
                    {overflowData.late.map(service => (
                      <OverflowServiceCard key={service.id} service={service} />
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Show message if no services in the entire period */}
            {filteredServices.length === 0 && (
              <div className="p-8 text-center">
                <Empty description={`No ${activeTab === "all" ? "" : activeTab} services scheduled for this period`} />
              </div>
            )}
          </div>
        </div>
        )}
      </Spin>
    </>
  );

  return (
    <>
      <div className="bg-white rounded-lg border p-4">
        <CalendarContent />
      </div>

      {/* Service Detail Modal */}
      <Modal
        title="Service Details"
        open={!!selectedService}
        onCancel={() => setSelectedService(null)}
        footer={null}
        width={800}
      >
        {selectedService && (
          <div className="cursor-pointer" onClick={() => {
            const bookingId = selectedService.jobTicketId || selectedService.jobTicket?.id;
            if (bookingId) {
              window.location.href = `/bookings/${bookingId}?tab=services&serviceId=${selectedService.id}`;
            }
          }}>
            <ContainerInfoCard
              serviceTicket={selectedService}
              variant="detailed"
              showHeader={true}
              className="border-0"
            />
          </div>
        )}
      </Modal>

      {/* Reschedule Modal */}
      <Modal
        title="Reschedule Service"
        open={rescheduleModalVisible}
        onOk={handleManualReschedule}
        onCancel={() => {
          setRescheduleModalVisible(false);
          setServiceToReschedule(null);
          setNewDateTime(null);
        }}
        okText="Reschedule"
        cancelText="Cancel"
        width={400}
      >
        {serviceToReschedule && (
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Service:</div>
              <div className="text-base font-semibold">{serviceToReschedule.serviceType}</div>
              <div className="text-sm text-gray-600">Serial: #{serviceToReschedule.serial}</div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Current Time:</div>
              <div className="text-sm text-gray-600">
                {dayjs(serviceToReschedule.startDatetime).format('MMM D, YYYY h:mm A')}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">New Date & Time:</div>
              <DatePicker
                showTime={{ format: 'h:mm A', use12Hours: true, minuteStep: 15 }}
                format="MMM D, YYYY h:mm A"
                value={newDateTime}
                onChange={(date) => setNewDateTime(date)}
                className="w-full"
                placeholder="Select new date and time"
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Mozart Confirmation Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Music size={20} className="text-purple-600" />
            <span>Confirm Bulk Update</span>
          </div>
        }
        open={confirmModalVisible}
        onOk={async () => {
          setConfirmModalVisible(false);
          if (confirmModalConfig.type === 'vessel') {
            await handleBulkUpdateVessel();
          } else if (confirmModalConfig.type === 'staff') {
            await handleBulkUpdateStaff();
          } else if (confirmModalConfig.type === 'datetime') {
            await handleBulkDateShift();
          }
        }}
        onCancel={() => setConfirmModalVisible(false)}
        okText="Confirm Update"
        cancelText="Cancel"
        width={600}
        okButtonProps={{ danger: true }}
      >
        <div className="space-y-4 py-4">
          {/* Header Summary */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-sm font-medium text-purple-900 mb-2">
              You are about to update {selectedServiceIds.size} service ticket{selectedServiceIds.size !== 1 ? 's' : ''}
            </div>
            <div className="text-xs text-purple-700">
              Selected services: {Array.from(selectedServiceIds).map(id => {
                const service = filteredServices.find(s => s.id === id);
                return service?.serial || id;
              }).join(', ')}
            </div>
          </div>

          {/* Update Details */}
          <div className="border-l-4 border-blue-500 pl-4">
            <div className="font-semibold text-gray-900 mb-2">Update Details:</div>

            {confirmModalConfig.type === 'vessel' && confirmModalConfig.data && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">New Vessel:</span>
                  <span className="font-medium text-gray-900">{confirmModalConfig.data.vesselName}</span>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  ℹ️ The vessel assignment will be updated for all selected service tickets
                </div>
              </div>
            )}

            {confirmModalConfig.type === 'staff' && confirmModalConfig.data && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">Operation:</span>
                  <span className="font-medium text-gray-900 capitalize">{confirmModalConfig.data.operation} Staff</span>
                </div>
                {confirmModalConfig.data.staffNames.length > 0 && (
                  <div className="mt-2">
                    <div className="text-gray-600 mb-1">Staff Members:</div>
                    <div className="flex flex-wrap gap-2">
                      {confirmModalConfig.data.staffNames.map((name: string, idx: number) => (
                        <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-2">
                  {confirmModalConfig.data.operation === 'add' && '➕ Staff will be added to existing assignments'}
                  {confirmModalConfig.data.operation === 'replace' && '🔄 All existing staff will be replaced'}
                  {confirmModalConfig.data.operation === 'remove' && '➖ Selected staff will be removed from assignments'}
                </div>
              </div>
            )}

            {confirmModalConfig.type === 'datetime' && confirmModalConfig.data && (
              <div className="space-y-2 text-sm">
                {confirmModalConfig.data.shiftType === 'specific' ? (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">New Date:</span>
                      <span className="font-medium text-gray-900">
                        {confirmModalConfig.data.specificDate?.format('MMM D, YYYY')}
                        {confirmModalConfig.data.specificTime && ` at ${confirmModalConfig.data.specificTime.format('h:mm A')}`}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      📅 All services will be moved to this date{confirmModalConfig.data.specificTime ? ' and time' : ' (preserving original times)'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Shift:</span>
                      <span className="font-medium text-gray-900">
                        {confirmModalConfig.data.shiftType === 'forward' ? '→' : '←'} {confirmModalConfig.data.shiftAmount} {confirmModalConfig.data.shiftUnit}{confirmModalConfig.data.shiftAmount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      ⏰ All services will be shifted {confirmModalConfig.data.shiftType === 'forward' ? 'forward' : 'backward'} by the specified duration
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-yellow-600 text-lg">⚠️</span>
              <div className="text-xs text-yellow-800">
                <strong>Warning:</strong> This action will immediately update all selected service tickets. This operation cannot be undone.
              </div>
            </div>
          </div>
        </div>
      </Modal>

    </>
  );
};